import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "./common/Header";
import Footer from "./common/Footer";
import LoginPopup from "./popup/LoginPopup";
import SEO from "../components/common/SEO";
import { getValidSession } from "./helpers/sessionUtils";
import { toast } from "react-toastify";
import ReviewModal from "./popup/ReviewModal";
import noDoctor from '../assets/images/no-user.png';
import noHospital from '../assets/images/no-hospital.png';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";


export default function MedicalDetail() {
  const { type, id: idRouteParam, slug: slugRouteParam } = useParams();
  const routeIdOrSlug = idRouteParam || slugRouteParam;
  const id = routeIdOrSlug ? (() => {
    if (/^\d+$/.test(routeIdOrSlug)) return routeIdOrSlug;
    const parts = routeIdOrSlug.split('-').filter(Boolean);
    const last = parts[parts.length - 1];
    return /^\d+$/.test(last) ? last : null;
  })() : null;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth State
  const [user, setUser] = useState(() => {
    const session = getValidSession();
    return session?.user?.user || session?.user || null;
  });

  // Form states for Enquiry Modal
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", message: "" });

  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showThanksModal, setShowThanksModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingReviewOpen, setPendingReviewOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Activity Lead Recording
  useEffect(() => {
    if (user && data && data.id && !sessionStorage.getItem(`activity_lead_${type}_${data.id}`)) {
      const sendActivityLead = async () => {
        try {
          const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
          const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
          
          await axios.post(`${API_URL}/enquiries/activity`, {
            type: type, // 'doctor' or 'hospital'
            type_id: data.id,
            mobile: user.phone || user.mobile || '',
            name: user.name || 'Guest',
            email: user.email || '',
            city: data.city || ''
          }, { headers });
          
          sessionStorage.setItem(`activity_lead_${type}_${data.id}`, 'true');
        } catch (e) {
          console.error("Activity lead error:", e);
        }
      };
      
      if (user.phone || user.mobile) {
        sendActivityLead();
      }
    }
  }, [user, data, type]);

  const handleAction = (e, actionType) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;

    setPendingAction(actionType);

    if (!user) {
      setShowLoginPopup(true);
      return;
    }

    processLead(actionType, user);
  };

  const handleLoginSuccess = (loggedInUser) => {
    const session = getValidSession();
    const resolvedUser = session?.user?.user || session?.user || loggedInUser;
    setUser(resolvedUser);
    setShowLoginPopup(false);

    if (pendingAction) {
      processLead(pendingAction, resolvedUser);
    } else if (pendingReviewOpen) {
      setPendingReviewOpen(false);
      setTimeout(() => {
        setShowReviewModal(true);
      }, 300);
    }
  };

  const processLead = async (actionType, userData = null) => {
    setIsSubmitting(true);
    const currentUser = userData || user;

    const payload = {
      name: currentUser?.name || "Guest",
      email: currentUser?.email || "",
      mobile: (currentUser?.phone || currentUser?.mobile || "").replace(/^(\+91|91)/, ""),
      type: type,
      type_id: data.id,
      message: `User clicked ${actionType} for ${displayName}`
    };

    let success = false;
    try {
      const res = await axios.post(`${API_URL}/enquiries/create`, payload);
      if (res.data.success) {
        success = true;
        setShowThanksModal(true);
      }
    } catch (err) {
      toast.error("Error processing request. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
      if (!success) {
        setPendingAction(null);
      }
    }
  };

  const handleFinalRedirect = (actionType) => {
    setShowThanksModal(false);
    const contactNumber = data.phone_number || data.whatsapp_number || data.hospital_phone_number || data.hospital_whatsapp_number;

    if (actionType === "call") {
      window.location.href = `tel:${contactNumber}`;
    } else if (actionType === "whatsapp") {
      window.open(`https://wa.me/${contactNumber}`, "_blank");
    }
    setPendingAction(null);
  };

  const [showReviewModal, setShowReviewModal] = useState(false);

  const openMedicalReviewModal = () => {
    if (!user) {
      setPendingReviewOpen(true);
      setShowLoginPopup(true);
      return;
    }
    setShowReviewModal(true);
  };

  useEffect(() => {
    fetchDetails();
    fetchReviews();
  }, [type, id]);

  // Autofill form if user is logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        mobile: (user.phone || user.mobile || "").replace(/^(\+91|91)/, "")
      }));
    }
  }, [user]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const url = type === "doctor" ? `${API_URL}/hospitals/public/doctor/${id}` : `${API_URL}/hospitals/public/hospital/${id}`;
      const res = await axios.get(url);
      if (res.data.success || res.data.status) setData(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchReviews = async () => {
    try {
      const url = type === "doctor" ? `${API_URL}/doctor-reviews/doctor/${id}` : `${API_URL}/hospital-reviews/hospital/${id}`;
      const res = await axios.get(url);
      if (res.data.success) {
        setReviews(res.data.data || []);
      }
    } catch (err) { console.error(err); }
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const payload = { ...formData, type, type_id: data.id };
    try {
      const res = await axios.post(`${API_URL}/enquiries/create`, payload);
      if (res.data.success) {
        toast.success("Success! Your enquiry has been sent.");
        setShowModal(false);
        setShowThanksModal(true);
      }
    } catch (err) { toast.error("Error sending enquiry."); }
    finally { setIsSubmitting(false); }
  };

  const getTodayStatus = () => {
    if (!data?.availability) return "Status Unavailable";
    try {
      const schedule = typeof data.availability === 'string' ? JSON.parse(data.availability) : data.availability;
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const today = days[new Date().getDay()];
      const todayInfo = schedule[today];
      if (!todayInfo || todayInfo.closed) return `Closed today (${today})`;
      return `Open today: ${todayInfo.slots[0].from} - ${todayInfo.slots[0].to}`;
    } catch (e) { return "Check Schedule"; }
  };

  if (loading) return <div className="text-center p-5 mt-5"><div className="spinner-border text-primary"></div></div>;
  if (!data) return <div className="text-center p-5">Details not found.</div>;

  const displayName = type === "doctor" ? data.doctor_name : data.hospital_name;
  const displayImage = type === "doctor"
    ? (data.doctor_images?.[0]?.image_url || "/assets/img/placeholders/doctor.png")
    : (data.hospital_images?.[0]?.image_url || "/assets/img/placeholders/hospital.png");
  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + Number(r.rating || 0), 0) / reviews.length
    : 0;

  return (
    <div className="medical-detail-page banner-content bg-light min-vh-100 d-flex flex-column">
      <SEO title={`${displayName} | Mangoloops`} description={data.bio?.substring(0, 160) || `Learn more about ${displayName}`} />
      <Header />

      <main className="container py-4 flex-grow-1">
        {/* --- PROFILE HEADER CARD --- */}
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
          <div className="row g-0">
            <div className="col-md-3 p-4 d-flex align-items-center justify-content-center bg-white">
              <div className="medical-main-photo-wrap w-100">
              <img
                src={displayImage}
                className="rounded-4 shadow-sm w-100"
                style={{ height: '240px', objectFit: 'cover' }}
                alt={displayName}
                onError={(e) => {
                  e.target.src = type === "doctor" ? "/assets/img/placeholders/doctor.png" : "/assets/img/placeholders/hospital.png";
                }}
              />
                <button
                  type="button"
                  className="detail-photo-review-badge"
                  onClick={openMedicalReviewModal}
                  aria-label="Open medical reviews"
                >
                  <span className="detail-photo-review-stars">
                    {[1, 2, 3, 4, 5].map(i => (
                      <i key={i} className={`bi bi-star${i <= Math.round(avgRating) ? '-fill' : ''} text-warning`}></i>
                    ))}
                  </span>
                  <span className="detail-photo-review-score">
                    {Number(avgRating || 0).toFixed(1)}/5
                  </span>
                  <span className="detail-photo-review-count">
                    ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                  </span>
                </button>
              </div>
            </div>

            <div className="col-md-6 p-4 bg-white border-md-start border-light">
              <h2 className="fw-bold text-info-emphasis mb-1 text-left" >{displayName}</h2>
              <p className="text-muted small mb-3"><i className="bi bi-geo-alt-fill text-danger me-2"></i>{data.city}, {data.state}</p>

              <div className="mb-2">
                <label className="text-uppercase fw-bold text-muted small" style={{ fontSize: '10px' }}>Speciality</label>
                <div className="fw-bold text-dark">{data.speciality_names || "General Physician"}</div>
              </div>

              <div className="mb-3">
                <label className="text-uppercase fw-bold text-muted small" style={{ fontSize: '10px' }}>Consultation Fee</label>
                <div className="fw-bold text-success fs-3">₹{data.consultation_fees}</div>
              </div>

              <div className="status-bar py-2 px-3 rounded-pill text-white fw-bold text-center shadow-sm d-inline-block w-100 mb-2" style={{ background: 'linear-gradient(90deg, #0dcaf0, #0aa2c0)' }}>
                <i className="bi bi-clock me-2"></i> {getTodayStatus()}
              </div>
            </div>

            <div className="col-md-3 p-4 bg-white border-start border-secondary-subtle d-flex flex-column align-items-center justify-content-center">
              <div
                className="text-center mb-3 review-stats-clickable medical-desktop-review-block"
                onClick={openMedicalReviewModal}
                title="Click to view reviews"
              >
                <div className="text-warning fs-5">
                  {[1, 2, 3, 4, 5].map(i => {
                    return <i key={i} className={`bi bi-star${i <= Math.round(avgRating) ? '-fill' : ''} me-1`}></i>;
                  })}
                  <span className="text-dark fw-bold ms-1">
                    {Number(avgRating || 0).toFixed(1)}/5
                  </span>
                </div>
                <div className="text-muted small">({reviews.length} reviews)</div>
                <div className="text-primary small fw-bold mt-1"><i className="bi bi-pencil-square me-1"></i>Write a Review</div>
              </div>

              <div className="row g-2 w-100 detail-action-buttons">
                <div className="col-12">
                  <button
                    onClick={(e) => handleAction(e, "call")}
                    disabled={isSubmitting}
                    className="btn btn-outline-primary fw-bold py-2 rounded-3 w-100 d-flex align-items-center justify-content-center"
                  >
                    {isSubmitting && pendingAction === "call" ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <i className="bi bi-telephone-fill me-2"></i>
                    )}
                    Call Now
                  </button>
                </div>
                <div className="col-12">
                  <button
                    onClick={(e) => handleAction(e, "whatsapp")}
                    disabled={isSubmitting}
                    className="btn btn-whatsapp text-white fw-bold py-2 rounded-3 w-100 d-flex align-items-center justify-content-center"
                  >
                    {isSubmitting && pendingAction === "whatsapp" ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <i className="bi bi-whatsapp me-2"></i>
                    )}
                    WhatsApp
                  </button>
                </div>
                <div className="col-12">
                  <button
                    onClick={(e) => handleAction(e, "book")}
                    disabled={isSubmitting}
                    className="btn btn-outline-dark fw-bold py-2 rounded-3 w-100 d-flex align-items-center justify-content-center listing-book-btn detail-book-action-btn"
                  >
                    {isSubmitting && pendingAction === "book" ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <i className="bi bi-calendar-check-fill me-2"></i>
                    )}
                    {isSubmitting && pendingAction === "book" ? "Processing..." : "Book Now"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- TABS SECTION --- */}
        <div className="card border-0 shadow-sm rounded-4 p-4 medical-about-card">
          <div className="animate-fade">
            <h5 className="fw-bold mb-3">About</h5>
            <p className="text-muted leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{data.bio || `Welcome to ${displayName}.`}</p>
          </div>
        </div>
      </main>

      {/* --- ENQUIRY MODAL --- */}
      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content card shadow-lg border-0 rounded-4">
            <div className="card-header bg-primary text-white d-flex justify-content-between p-3 rounded-top-4">
              <h6 className="mb-0 fw-bold">Enquire Now</h6>
              <button className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleEnquirySubmit}>
                <input type="text" name="name" className="form-control mb-3" placeholder="Full Name" value={formData.name} required onChange={handleInputChange} />
                <input type="email" name="email" className="form-control mb-3" placeholder="Email" value={formData.email} required onChange={handleInputChange} />
                <input type="tel" name="mobile" className="form-control mb-3" placeholder="Mobile" value={formData.mobile} required onChange={handleInputChange} />
                <textarea name="message" className="form-control mb-3" rows="3" placeholder="Message" value={formData.message} required onChange={handleInputChange}></textarea>
                <button type="submit" className="btn btn-primary w-100 fw-bold" disabled={isSubmitting}>{isSubmitting ? "Sending..." : "Submit Enquiry"}</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --- REVIEW MODAL --- */}
      {showReviewModal && data && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          entity={data}
          entityType={type}
          user={user}
          apiEndpoint={type === 'doctor' ? '/doctor-reviews' : '/hospital-reviews'}
          onReviewSubmitted={fetchReviews}
        />
      )}

      {/* --- THANK YOU MODAL --- */}
      {(isSubmitting || showThanksModal) && !showLoginPopup && (
        <div className="custom-modal-overlay" style={{ zIndex: 9999999 }}>
          <div className="thanks-modal card shadow-lg border-0 rounded-4 text-center p-5" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            {isSubmitting && !showThanksModal ? (
              <>
                <div className="mb-4">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                    <span className="visually-hidden">Processing...</span>
                  </div>
                </div>
                <h4 className="fw-bold mb-2">Processing your request...</h4>
                <p className="text-muted mb-0">Please wait while we save your lead and notify the vendor.</p>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <div className="success-checkmark-wrap">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                  </div>
                </div>
                <h2 className="fw-bold mb-3">Thank You!</h2>
                <p className="text-muted mb-4">Your request has been sent to the team. We will get back to you shortly.</p>
                <button className="btn btn-primary rounded-pill px-4 py-2 fw-bold" onClick={() => handleFinalRedirect(pendingAction)}>
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* --- LOGIN POPUP MODAL --- */}
      {showLoginPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }} onClick={() => {
          setShowLoginPopup(false);
          setIsSubmitting(false);
          setPendingAction(null);
          setPendingReviewOpen(false);
        }}>
          <div onClick={(e) => e.stopPropagation()}>
            <LoginPopup
              visible={showLoginPopup}
              onClose={() => {
                setShowLoginPopup(false);
                setIsSubmitting(false);
                setPendingAction(null);
                setPendingReviewOpen(false);
              }}
              onLoginSuccess={handleLoginSuccess}
              serviceType={type === 'doctor' ? 'Doctor' : 'Hospital'}
            />
          </div>
        </div>
      )}

      {/* Mobile Sticky Bottom Bar */}
      <div className={`holiday-mobile-sticky-bar ${showStickyBar ? 'visible' : ''}`}>
        <div className="holiday-sticky-price-info">
          <div className="holiday-sticky-price-row">
            <span className="holiday-sticky-price-current">
              ₹{data?.consultation_fees ? Number(data.consultation_fees).toLocaleString() : 'Price on request'}
            </span>
          </div>
          {data?.consultation_fees && (
            <div className="holiday-sticky-taxes-label">
              Consultation Fee
            </div>
          )}
        </div>
        <div className="holiday-sticky-action-container">
          <button
            className="holiday-sticky-book-btn"
            onClick={(e) => handleAction(e, "query")}
            disabled={isSubmitting}
          >
            {isSubmitting && pendingAction === "query" ? (
              <>
                <span className="spinner-border spinner-border-sm"></span>
                <span className="btn-title-main">Processing...</span>
              </>
            ) : (
              <span className="btn-title-main">Enquire Now</span>
            )}
          </button>
        </div>
      </div>

      <Footer />

      <style>{`
        .medical-detail-page #header {
          background: #fff;
          box-shadow: 0 2px 14px rgba(15, 23, 42, 0.12);
          position: relative;
          z-index: 10;
        }
        .medical-detail-page #header .u-header__section {
          background: #fff !important;
          box-shadow: 0 6px 22px rgba(15, 23, 42, 0.14);
        }
        .cursor-pointer { cursor: pointer; }
        .btn-whatsapp { background-color: #00c897; border: none; transition: all 0.3s ease; }
        .btn-whatsapp:hover { background-color: #00b386; color: white; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 200, 151, 0.2); }
        .btn-enquire { background-color: #3eb0e9; border: none; transition: all 0.3s ease; }
        .btn-enquire:hover { background-color: #319ed4; color: white; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(62, 176, 233, 0.2); }
        .btn-outline-primary:hover { transform: translateY(-2px); }
        .listing-book-btn {
          background: transparent !important;
          color: #212529 !important;
          border: 1px solid #212529 !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
        }
        .listing-book-btn:hover {
          background: #212529 !important;
          color: #fff !important;
          border-color: #212529 !important;
          transform: translateY(-2px);
        }
        .review-stats-clickable {
          cursor: pointer;
          padding: 12px 8px;
          border-radius: 12px;
          transition: all 0.25s ease;
        }
        .review-stats-clickable:hover {
          background: #f0f7ff;
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(13, 110, 253, 0.12);
        }
        .medical-main-photo-wrap {
          position: relative;
          overflow: hidden;
          border-radius: 16px;
        }
        .detail-photo-review-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          display: none;
          align-items: center;
          gap: 5px;
          max-width: calc(100% - 20px);
          padding: 6px 8px;
          border: 0;
          border-radius: 5px;
          background: rgba(17, 24, 39, 0.72);
          color: #fff;
          cursor: pointer;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
          backdrop-filter: blur(3px);
          line-height: 1;
          z-index: 2;
        }
        .detail-photo-review-badge:hover {
          background: rgba(17, 24, 39, 0.84);
        }
        .detail-photo-review-stars {
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
        }
        .detail-photo-review-stars i {
          margin-right: 2px !important;
          font-size: 15px;
        }
        .detail-photo-review-score {
          font-weight: 700;
          font-size: 12px;
          white-space: nowrap;
        }
        .detail-photo-review-count {
          display: none;
        }
        @media (max-width: 767px) {
          .medical-desktop-review-block,
          .detail-action-buttons {
            display: none !important;
          }
          .medical-detail-page main {
            padding-bottom: 96px !important;
          }
          .detail-photo-review-badge {
            display: inline-flex;
          }
        }
        .status-bar { transition: transform 0.3s ease; }
        .status-bar:hover { transform: scale(1.02); }
        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center;
          z-index: 9999; padding: 15px;
          animation: fadeIn 0.2s ease;
        }
        .custom-modal-content { 
          width: 100%; max-width: 500px; 
          animation: slideUp 0.3s ease;
        }
        .thanks-modal {
          animation: slideUp 0.3s ease;
          background: white;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Custom Spinner */
        .spinner-border {
          display: inline-block;
          width: 1rem;
          height: 1rem;
          vertical-align: text-bottom;
          border: 0.2em solid currentColor;
          border-right-color: transparent;
          border-radius: 50%;
          animation: spinner-border .75s linear infinite;
        }
        @keyframes spinner-border {
          to { transform: rotate(360deg); }
        }
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
          border-width: 0.2em;
        }
      `}</style>
    </div>
  );
}
